import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CalendarClock,
  Check,
  ClipboardList,
  FileText,
  Loader2,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Settings,
  ShieldAlert,
  Sparkles,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import EmailMarketingVisualEditor, { DEFAULT_EMAIL_DESIGN, normalizeEmailBlocks } from "./EmailMarketingVisualEditor";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_CAMPAIGN = {
  nome: "", oggetto: "", preheader: "", blocks: [], design: { ...DEFAULT_EMAIL_DESIGN },
  list_ids: [], kind: "prospect", scheduled_at: "",
};
const EMPTY_CONTACT = { email: "", nome: "", organizzazione: "", fonte: "manuale", base_giuridica: "interesse_legittimo", tipo_contatto: "prospect", tags: "" };
const EMPTY_LIST = { nome: "", description: "", contact_ids: [] };
const EMPTY_BANDO = { titolo: "", ente: "", scadenza: "", regione: "", importo: "", url: "", riassunto: "" };

function asBlocks(value) {
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value || "[]"); } catch { return []; }
}

function dateTimeValue(value) {
  return value ? String(value).slice(0, 16) : "";
}

function parseCsvLine(line, delimiter) {
  const values = [];
  let value = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"' && line[index + 1] === '"') {
      value += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      values.push(value.trim());
      value = "";
    } else {
      value += char;
    }
  }
  values.push(value.trim());
  return values;
}

function formatEmailFrom(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const email = value.email || value.address || "";
    const name = value.name || "";
    return name && email ? `${name} <${email}>` : email || name || "";
  }
  return String(value);
}

function statusBadge(value) {
  const label = value || "bozza";
  const palette = {
    sent: "bg-emerald-100 text-emerald-700",
    inviato: "bg-emerald-100 text-emerald-700",
    inviata: "bg-emerald-100 text-emerald-700",
    scheduled: "bg-amber-100 text-amber-700",
    programmata: "bg-amber-100 text-amber-700",
    in_elaborazione: "bg-amber-100 text-amber-800",
    nuovo: "bg-blue-100 text-blue-800",
    approvato: "bg-emerald-100 text-emerald-700",
    scartato: "bg-slate-200 text-slate-600",
    errore: "bg-red-100 text-red-700",
    fallita: "bg-red-100 text-red-700",
  };
  return <Badge className={palette[label] || "bg-slate-100 text-slate-700"}>{label.replaceAll("_", " ")}</Badge>;
}

const STATO_INVIO_LABEL = {
  in_invio: "In invio",
  inviata: "Inviata",
  consegnata: "Consegnata",
  aperta: "Aperta",
  cliccata: "Cliccata",
  bounce: "Bounce",
  complaint: "Reclamo",
  fallita: "Fallita",
};

function campaignStatRows(data) {
  if (Array.isArray(data)) {
    return data
      .map((item) => ({ stato: item?.stato || item?._id || "", count: Number(item?.count) || 0 }))
      .filter((item) => item.stato);
  }
  if (data && typeof data === "object") {
    return Object.entries(data)
      .filter(([, value]) => value !== undefined && typeof value !== "object")
      .map(([stato, count]) => ({ stato, count: Number(count) || 0 }));
  }
  return [];
}

export default function EmailMarketingAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sources, setSources] = useState([]);
  const [bandi, setBandi] = useState([]);
  const [config, setConfig] = useState({ from_clienti: "", from_prospect: "", reply_to: "", footer_html: "", news_inbox: "", news_enabled: false });
  const [campaign, setCampaign] = useState(EMPTY_CAMPAIGN);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [contactDialog, setContactDialog] = useState(false);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [editingContact, setEditingContact] = useState(null);
  const [listDialog, setListDialog] = useState(false);
  const [listForm, setListForm] = useState(EMPTY_LIST);
  const [editingList, setEditingList] = useState(null);
  const [bandoDialog, setBandoDialog] = useState(false);
  const [bandoForm, setBandoForm] = useState(EMPTY_BANDO);
  const [editingBando, setEditingBando] = useState(null);
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [stats, setStats] = useState(null);
  const contactsCsvInput = useRef(null);

  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API}/api/admin/commerciale${path}`, { headers, ...options });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(json.detail || json.message || "Operazione non riuscita");
    return json;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [contactData, listData, campaignData, templateData, sourceData, bandiData, configData] = await Promise.all([
        request("/marketing/contacts"), request("/marketing/lists"), request("/marketing/campaigns"),
        request("/marketing/templates"), request("/marketing/sources"), request("/marketing/bandi"), request("/marketing/config"),
      ]);
      setContacts(contactData.data || []);
      setLists(listData.data || []);
      setCampaigns(campaignData.data || []);
      setTemplates(templateData.data || []);
      setSources(sourceData.data || []);
      setBandi(bandiData.data || []);
      setConfig((current) => ({ ...current, ...(configData.data || configData || {}) }));
    } catch (error) {
      toast.error(error.message || "Impossibile caricare il marketing");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const selectedListCount = useMemo(
    () => lists
      .filter((list) => campaign.list_ids.includes(list.id))
      .reduce((total, list) => total + Number(list.contact_count ?? list.contacts?.length ?? 0), 0),
    [campaign.list_ids, lists],
  );

  const run = async (key, callback) => {
    setBusy(key);
    try { await callback(); } catch (error) { toast.error(error.message || "Operazione non riuscita"); } finally { setBusy(""); }
  };

  const setCampaignField = (field, value) => setCampaign((current) => ({ ...current, [field]: value }));
  const newCampaign = () => { setEditingCampaign(null); setCampaign({ ...EMPTY_CAMPAIGN, design: { ...DEFAULT_EMAIL_DESIGN } }); setStats(null); };
  const editCampaign = (item) => {
    setEditingCampaign(item);
    setCampaign({
      nome: item.nome || "", oggetto: item.oggetto || "", preheader: item.preheader || "",
      blocks: normalizeEmailBlocks(asBlocks(item.blocks)),
      design: { ...DEFAULT_EMAIL_DESIGN, ...(item.design || {}) },
      list_ids: item.list_ids || item.lists?.map((list) => list.id) || [],
      kind: item.kind || "prospect", scheduled_at: dateTimeValue(item.scheduled_at),
    });
    setStats(null);
  };
  const campaignPayload = () => ({ ...campaign, scheduled_at: campaign.scheduled_at || null });

  const saveCampaign = async () => run("campaign-save", async () => {
    if (!campaign.nome.trim() || !campaign.oggetto.trim()) throw new Error("Nome e oggetto sono obbligatori");
    await request(editingCampaign ? `/marketing/campaigns/${editingCampaign.id}` : "/marketing/campaigns", {
      method: editingCampaign ? "PUT" : "POST", body: JSON.stringify(campaignPayload()),
    });
    toast.success(editingCampaign ? "Campagna aggiornata" : "Bozza salvata");
    await loadAll();
  });

  const campaignAction = async (action) => run(`campaign-${action}`, async () => {
    if (!editingCampaign) throw new Error("Salva prima la bozza della campagna");
    if (action === "test") {
      if (!testEmail.trim()) throw new Error("Inserisci l'email per il test");
      const result = await request(`/marketing/campaigns/${editingCampaign.id}/test`, { method: "POST", body: JSON.stringify({ email: testEmail.trim() }) });
      if (result.failed) throw new Error(result.detail || "Invio di test fallito");
      toast.success("Email di test inviata");
    }
    if (action === "send") {
      if (!window.confirm("Inviare ora questa campagna alle liste selezionate?")) return;
      await request(`/marketing/campaigns/${editingCampaign.id}/send`, { method: "POST" });
      toast.success("Invio avviato"); await loadAll();
    }
    if (action === "schedule") {
      if (!campaign.scheduled_at) throw new Error("Scegli data e ora di invio");
      await request(`/marketing/campaigns/${editingCampaign.id}/schedule`, { method: "POST", body: JSON.stringify({ scheduled_at: campaign.scheduled_at }) });
      toast.success("Campagna programmata"); await loadAll();
    }
  });

  const openContact = (item) => {
    setEditingContact(item || null);
    setContactForm(item ? { ...EMPTY_CONTACT, ...item, tags: Array.isArray(item.tags) ? item.tags.join(", ") : (item.tags || "") } : EMPTY_CONTACT);
    setContactDialog(true);
  };
  const saveContact = async () => run("contact-save", async () => {
    if (!contactForm.email.trim() || !contactForm.fonte.trim() || !contactForm.base_giuridica.trim()) throw new Error("Email, fonte e base giuridica sono obbligatorie");
    const body = { ...contactForm, email: contactForm.email.trim(), tags: String(contactForm.tags).split(",").map((tag) => tag.trim()).filter(Boolean) };
    await request(editingContact ? `/marketing/contacts/${editingContact.id}` : "/marketing/contacts", { method: editingContact ? "PATCH" : "POST", body: JSON.stringify(body) });
    toast.success(editingContact ? "Contatto aggiornato" : "Contatto creato");
    setContactDialog(false); await loadAll();
  });
  const syncContacts = (endpoint, success) => run(endpoint, async () => {
    const result = await request(`/marketing/contacts/${endpoint}`, { method: "POST" });
    toast.success(`${success}: ${result.created || 0} creati${result.updated !== undefined ? `, ${result.updated} aggiornati` : result.skipped !== undefined ? `, ${result.skipped} ignorati` : ""}`);
    await loadAll();
  });

  const importContactsCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await run("contacts-csv", async () => {
      const lines = (await file.text()).split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) throw new Error("Il CSV deve contenere intestazione e almeno una riga.");
      const delimiter = lines[0].includes(";") ? ";" : ",";
      const keys = parseCsvLine(lines[0], delimiter).map((item) => item.toLowerCase().trim().replace(/\s+/g, "_"));
      const rows = lines.slice(1).map((line) => {
        const values = parseCsvLine(line, delimiter);
        const item = Object.fromEntries(keys.map((key, index) => [key, values[index] || ""]));
        return {
          email: item.email || "",
          nome: item.nome || item.referente || "",
          organizzazione: item.organizzazione || item.struttura || item.nome_struttura || "",
          fonte: item.fonte || "import_csv",
          base_giuridica: item.base_giuridica || "interesse_legittimo",
          tipo_contatto: item.tipo_contatto || "prospect",
          tags: item.tags || "",
        };
      });
      const result = await request("/marketing/contacts/import", { method: "POST", body: JSON.stringify({ rows }) });
      toast.success(`CSV importato: ${result.created || 0} contatti, ${result.skipped || 0} scartati`);
      await loadAll();
    });
  };

  const openList = (item) => {
    setEditingList(item || null);
    setListForm(item ? { nome: item.nome || "", description: item.description || "", contact_ids: item.contact_ids || item.contacts?.map((contact) => contact.id) || [] } : EMPTY_LIST);
    setListDialog(true);
  };
  const saveList = async () => run("list-save", async () => {
    if (!listForm.nome.trim()) throw new Error("Il nome della lista è obbligatorio");
    await request(editingList ? `/marketing/lists/${editingList.id}` : "/marketing/lists", { method: editingList ? "PUT" : "POST", body: JSON.stringify({ ...listForm, nome: listForm.nome.trim() }) });
    toast.success(editingList ? "Lista aggiornata" : "Lista creata"); setListDialog(false); await loadAll();
  });
  const deleteList = async (item) => {
    if (!window.confirm(`Eliminare la lista "${item.nome}"?`)) return;
    await run(`list-delete-${item.id}`, async () => { await request(`/marketing/lists/${item.id}`, { method: "DELETE" }); toast.success("Lista eliminata"); await loadAll(); });
  };

  const moderateSource = async (source, stato) => run(`source-${source.id}`, async () => {
    await request(`/marketing/sources/${source.id}`, { method: "PATCH", body: JSON.stringify({ stato }) });
    toast.success("Stato sorgente aggiornato"); await loadAll();
  });
  const generateSourceBlock = async (source) => run(`source-block-${source.id}`, async () => {
    const result = await request(`/marketing/sources/${source.id}/genera-blocco`, { method: "POST" });
    if (result.block) {
      setCampaign((current) => ({ ...current, blocks: [...current.blocks, result.block] }));
      toast.success("Blocco aggiunto alla bozza corrente");
    } else toast.success("Blocco generato");
  });
  const reprocessSource = async (source) => run(`source-retry-${source.id}`, async () => {
    await request(`/marketing/sources/${source.id}/riprocessa`, { method: "POST" });
    toast.success("Fonte rielaborata");
    await loadAll();
  });

  const openBando = (item) => {
    setEditingBando(item || null);
    setBandoForm(item ? { ...EMPTY_BANDO, ...item, scadenza: String(item.scadenza || "").slice(0, 10) } : EMPTY_BANDO);
    setBandoDialog(true);
  };
  const saveBando = async () => run("bando-save", async () => {
    if (!bandoForm.titolo.trim()) throw new Error("Il titolo del bando è obbligatorio");
    await request(editingBando ? `/marketing/bandi/${editingBando.id}` : "/marketing/bandi", { method: editingBando ? "PUT" : "POST", body: JSON.stringify(bandoForm) });
    toast.success(editingBando ? "Bando aggiornato" : "Bando aggiunto"); setBandoDialog(false); await loadAll();
  });
  const deleteBando = async (item) => {
    if (!window.confirm(`Eliminare "${item.titolo}"?`)) return;
    await run(`bando-delete-${item.id}`, async () => { await request(`/marketing/bandi/${item.id}`, { method: "DELETE" }); toast.success("Bando eliminato"); await loadAll(); });
  };
  const saveConfig = async () => run("config-save", async () => {
    await request("/marketing/config", { method: "PUT", body: JSON.stringify(config) });
    toast.success("Impostazioni email salvate");
  });

  const applyTemplate = (template) => {
    setCampaign((current) => ({
      ...current,
      blocks: normalizeEmailBlocks(asBlocks(template.blocks)),
      design: { ...DEFAULT_EMAIL_DESIGN, ...(template.design || {}) },
    }));
    toast.success(`Modello "${template.nome}" caricato nella bozza`);
  };
  const saveTemplate = async (template) => run(template.id ? `template-update-${template.id}` : "template-create", async () => {
    if (!template.nome?.trim()) throw new Error("Il nome del modello è obbligatorio");
    await request(template.id ? `/marketing/templates/${template.id}` : "/marketing/templates", {
      method: template.id ? "PUT" : "POST",
      body: JSON.stringify({ nome: template.nome.trim(), blocks: template.blocks, design: template.design }),
    });
    toast.success(template.id ? "Modello aggiornato" : "Modello salvato");
    await loadAll();
  });
  const deleteTemplate = async (template) => {
    if (!window.confirm(`Eliminare il modello "${template.nome}"?`)) return;
    await run(`template-delete-${template.id}`, async () => {
      await request(`/marketing/templates/${template.id}`, { method: "DELETE" });
      toast.success("Modello eliminato");
      await loadAll();
    });
  };
  const toggleCampaignList = (id, checked) => setCampaign((current) => ({ ...current, list_ids: checked ? [...current.list_ids, id] : current.list_ids.filter((listId) => listId !== id) }));

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={Mail}
        title="Email marketing"
        description="Contatti, campagne e contenuti commerciali. Verifica sempre servizio e destinatari della campagna: CasaDiRiposoPerTe e R&CO./Affidya non vanno mischiati."
        actions={<Button variant="outline" size="sm" disabled={loading} onClick={loadAll}><RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Aggiorna</Button>}
      />

      <Tabs defaultValue="campaigns">
        <TabsList className="h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="campaigns"><Mail className="mr-1 h-4 w-4" />Campagne</TabsTrigger>
          <TabsTrigger value="contacts"><Users className="mr-1 h-4 w-4" />Contatti</TabsTrigger>
          <TabsTrigger value="lists"><ClipboardList className="mr-1 h-4 w-4" />Liste</TabsTrigger>
          <TabsTrigger value="content"><FileText className="mr-1 h-4 w-4" />Contenuti</TabsTrigger>
          <TabsTrigger value="settings"><Settings className="mr-1 h-4 w-4" />Impostazioni</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-4">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0"><div><CardTitle className="text-base">{editingCampaign ? "Editor campagna" : "Nuova bozza"}</CardTitle><p className="mt-1 text-sm font-normal text-slate-500">Crea il template dell&apos;email graficamente, con blocchi trascinabili e impostazioni di stile.</p></div><Button variant="outline" size="sm" onClick={newCampaign}><Plus className="mr-1 h-4 w-4" />Nuova</Button></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5"><Label>Nome interno *</Label><Input value={campaign.nome} onChange={(event) => setCampaignField("nome", event.target.value)} /></div>
                <div className="space-y-1.5"><Label>Oggetto *</Label><Input value={campaign.oggetto} onChange={(event) => setCampaignField("oggetto", event.target.value)} /></div>
                <div className="space-y-1.5"><Label>Preheader</Label><Input value={campaign.preheader} onChange={(event) => setCampaignField("preheader", event.target.value)} /></div>
                <div className="space-y-1.5"><Label>Destinatari</Label><Select value={campaign.kind} onValueChange={(value) => setCampaignField("kind", value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="prospect">Prospect B2B</SelectItem><SelectItem value="clienti">Clienti con consenso</SelectItem></SelectContent></Select></div>
              </div>
              <EmailMarketingVisualEditor
                blocks={normalizeEmailBlocks(campaign.blocks)}
                design={{ ...DEFAULT_EMAIL_DESIGN, ...(campaign.design || {}) }}
                templates={templates}
                campaignName={campaign.nome}
                onBlocksChange={(blocks) => setCampaignField("blocks", blocks)}
                onDesignChange={(design) => setCampaignField("design", design)}
                onApplyTemplate={applyTemplate}
                onSaveTemplate={saveTemplate}
                onDeleteTemplate={deleteTemplate}
              />
              <div className="space-y-2"><Label>Liste destinatari ({selectedListCount} contatti selezionati)</Label><div className="grid gap-2 rounded-lg border p-3 sm:grid-cols-2">{lists.map((list) => <label key={list.id} className="flex items-center gap-2 text-sm"><Checkbox checked={campaign.list_ids.includes(list.id)} onCheckedChange={(checked) => toggleCampaignList(list.id, checked === true)} />{list.nome} <span className="text-slate-400">({list.contact_count ?? list.contacts?.length ?? 0})</span></label>)}{!lists.length && <p className="text-sm text-slate-500">Crea prima una lista di destinatari.</p>}</div></div>
              <div className="flex flex-wrap items-end gap-2 border-t pt-4">
                <Button onClick={saveCampaign} disabled={Boolean(busy)}>{busy === "campaign-save" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}Salva bozza</Button>
                <div className="flex gap-2"><Input className="w-56" type="email" placeholder="Email per test" value={testEmail} onChange={(event) => setTestEmail(event.target.value)} /><Button variant="outline" disabled={Boolean(busy) || !editingCampaign} onClick={() => campaignAction("test")}><Send className="mr-1 h-4 w-4" />Test</Button></div>
                <Input className="w-56" type="datetime-local" value={campaign.scheduled_at} onChange={(event) => setCampaignField("scheduled_at", event.target.value)} />
                <Button variant="outline" disabled={Boolean(busy) || !editingCampaign} onClick={() => campaignAction("schedule")}><CalendarClock className="mr-1 h-4 w-4" />Programma</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={Boolean(busy) || !editingCampaign} onClick={() => campaignAction("send")}><Send className="mr-1 h-4 w-4" />Invia ora</Button>
              </div>
            </CardContent>
          </Card>
          <div className="grid gap-3 lg:grid-cols-2">{campaigns.map((item) => {
            const rows = stats?.id === item.id ? campaignStatRows(stats.data) : null;
            return (
              <Card key={item.id}>
                <CardContent className="flex items-center justify-between gap-3 p-4">
                  <div>
                    <p className="font-medium">{item.nome || "Senza nome"}</p>
                    <p className="mt-1 text-sm text-slate-500">{item.oggetto || "Senza oggetto"}</p>
                    <div className="mt-2">{statusBadge(item.stato || item.status)}</div>
                  </div>
                  <div className="flex">
                    <Button variant="ghost" size="sm" onClick={() => editCampaign(item)}><Pencil className="mr-1 h-4 w-4" />Apri</Button>
                    <Button variant="ghost" size="sm" onClick={() => run(`stats-${item.id}`, async () => {
                      const result = await request(`/marketing/campaigns/${item.id}/stats`);
                      setStats({ id: item.id, data: result.data || result });
                    })}>Statistiche</Button>
                  </div>
                </CardContent>
                {rows && (
                  <CardContent className="border-t pb-4 pt-3 text-sm text-slate-600">
                    {rows.length
                      ? rows.map((row) => (
                        <span key={row.stato} className="mr-4">
                          <strong>{STATO_INVIO_LABEL[row.stato] || row.stato}:</strong> {row.count}
                        </span>
                      ))
                      : "Nessun evento registrato. L'invio di prova non produce statistiche."}
                  </CardContent>
                )}
              </Card>
            );
          })}</div>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4">
          <div className="flex flex-wrap gap-2"><input ref={contactsCsvInput} className="hidden" type="file" accept=".csv,text/csv" onChange={importContactsCsv} /><Button onClick={() => openContact()}><Plus className="mr-1 h-4 w-4" />Nuovo contatto</Button><Button variant="outline" disabled={Boolean(busy)} onClick={() => syncContacts("sync-clienti", "Sincronizzazione completata")}><Users className="mr-1 h-4 w-4" />Sincronizza clienti</Button><Button variant="outline" disabled={Boolean(busy)} onClick={() => syncContacts("import-directory", "Importazione completata")}><Upload className="mr-1 h-4 w-4" />Importa directory</Button><Button variant="outline" disabled={Boolean(busy)} onClick={() => contactsCsvInput.current?.click()}><Upload className="mr-1 h-4 w-4" />Importa CSV</Button></div>
          <div className="grid gap-3 lg:grid-cols-2">{contacts.map((contact) => <Card key={contact.id}><CardContent className="flex items-start justify-between gap-3 p-4"><div><p className="font-medium">{contact.nome || contact.email}</p><p className="text-sm text-slate-500">{contact.email}{contact.organizzazione ? ` · ${contact.organizzazione}` : ""}</p><div className="mt-2 flex flex-wrap gap-1">{(contact.unsubscribed_at || contact.opt_out) && <Badge className="bg-amber-100 text-amber-700">Opt-out</Badge>}{(contact.suppressed_at || contact.suppressed || contact.suppression) && <Badge className="bg-red-100 text-red-700">Soppresso</Badge>}<Badge variant="outline">{contact.fonte || "Fonte mancante"}</Badge><Badge variant="outline">{contact.base_giuridica || "Base mancante"}</Badge></div></div><Button variant="ghost" size="icon" onClick={() => openContact(contact)} aria-label="Modifica contatto"><Pencil className="h-4 w-4" /></Button></CardContent></Card>)}</div>
          {!loading && !contacts.length && <Card><CardContent className="p-8 text-center text-sm text-slate-500">Nessun contatto disponibile.</CardContent></Card>}
        </TabsContent>

        <TabsContent value="lists" className="space-y-4">
          <Button onClick={() => openList()}><Plus className="mr-1 h-4 w-4" />Nuova lista</Button>
          <div className="grid gap-3 lg:grid-cols-2">{lists.map((list) => <Card key={list.id}><CardContent className="flex items-center justify-between gap-3 p-4"><div><p className="font-medium">{list.nome}</p><p className="text-sm text-slate-500">{list.description || "Nessuna descrizione"} · {list.contact_count ?? list.contacts?.length ?? 0} contatti</p></div><div className="flex"><Button variant="ghost" size="icon" onClick={() => openList(list)}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="icon" className="text-red-600" disabled={Boolean(busy)} onClick={() => deleteList(list)}><Trash2 className="h-4 w-4" /></Button></div></CardContent></Card>)}</div>
        </TabsContent>

        <TabsContent value="content" className="space-y-5">
          <section className="space-y-3"><div className="flex items-center justify-between"><h3 className="font-semibold">Coda sorgenti</h3><Badge variant="outline">{sources.length} elementi</Badge></div>{sources.map((source) => {
            const titoloAi = source.titolo || source.ai?.titolo || "";
            const riassuntoAi = source.riassunto || source.ai?.riassunto || "";
            const oggetto = source.subject || "";
            const testoMail = source.raw_text || "";
            const mittente = formatEmailFrom(source.from);
            const canRetry = source.stato === "errore" || source.stato === "in_elaborazione";
            return (
              <Card key={source.id}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{oggetto || titoloAi || "Sorgente senza titolo"}</p>
                      {mittente && <p className="mt-1 text-xs text-slate-500">Da: {mittente}</p>}
                      <div className="mt-2">{statusBadge(source.stato)}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {canRetry && <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => reprocessSource(source)}><RefreshCw className="mr-1 h-4 w-4" />Riprova</Button>}
                      <Button size="sm" variant="outline" disabled={Boolean(busy) || canRetry} onClick={() => moderateSource(source, "approvato")}><Check className="mr-1 h-4 w-4" />Approva</Button>
                      <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => moderateSource(source, "scartato")}><ShieldAlert className="mr-1 h-4 w-4" />Scarta</Button>
                      <Button size="sm" disabled={Boolean(busy) || canRetry} onClick={() => generateSourceBlock(source)}><Sparkles className="mr-1 h-4 w-4" />Genera blocco</Button>
                    </div>
                  </div>
                  {oggetto && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Oggetto originale</p>
                      <p className="mt-1 text-sm text-slate-800">{oggetto}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Testo della mail</p>
                    {testoMail
                      ? <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border bg-slate-50 p-3 text-sm text-slate-700">{testoMail}</pre>
                      : <p className="mt-1 text-sm text-slate-400">Nessun testo originale disponibile.</p>}
                  </div>
                  {(titoloAi || riassuntoAi) && (titoloAi !== oggetto || riassuntoAi) && (
                    <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Bozza AI</p>
                      {titoloAi && <p className="mt-1 font-medium text-slate-900">{titoloAi}</p>}
                      {riassuntoAi && <p className="mt-1 text-sm text-slate-600">{riassuntoAi}</p>}
                    </div>
                  )}
                  {source.errore && source.stato === "errore" && <p className="text-xs text-red-600">{source.errore}</p>}
                </CardContent>
              </Card>
            );
          })}</section>
          <section className="space-y-3"><div className="flex items-center justify-between"><h3 className="font-semibold">Bandi</h3><Button size="sm" onClick={() => openBando()}><Plus className="mr-1 h-4 w-4" />Aggiungi bando</Button></div><div className="grid gap-3 lg:grid-cols-2">{bandi.map((bando) => <Card key={bando.id}><CardContent className="flex items-start justify-between gap-2 p-4"><div><p className="font-medium">{bando.titolo}</p><p className="text-sm text-slate-500">{[bando.ente, bando.regione, bando.scadenza && `Scade ${bando.scadenza}`].filter(Boolean).join(" · ")}</p>{bando.importo && <Badge className="mt-2" variant="outline">{bando.importo}</Badge>}</div><div className="flex"><Button variant="ghost" size="icon" onClick={() => openBando(bando)}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="icon" className="text-red-600" onClick={() => deleteBando(bando)}><Trash2 className="h-4 w-4" /></Button></div></CardContent></Card>)}</div></section>
        </TabsContent>

        <TabsContent value="settings"><Card><CardHeader><CardTitle className="text-base">Configurazione invii</CardTitle></CardHeader><CardContent className="space-y-4"><div className="grid gap-3 md:grid-cols-2"><div className="space-y-1.5"><Label>Mittente clienti</Label><Input value={config.from_clienti || ""} onChange={(event) => setConfig((current) => ({ ...current, from_clienti: event.target.value }))} /></div><div className="space-y-1.5"><Label>Mittente prospect</Label><Input value={config.from_prospect || ""} onChange={(event) => setConfig((current) => ({ ...current, from_prospect: event.target.value }))} /></div><div className="space-y-1.5"><Label>Reply-to</Label><Input type="email" value={config.reply_to || ""} onChange={(event) => setConfig((current) => ({ ...current, reply_to: event.target.value }))} /></div><div className="space-y-1.5"><Label>Casella news</Label><Input type="email" value={config.news_inbox || ""} onChange={(event) => setConfig((current) => ({ ...current, news_inbox: event.target.value }))} /></div></div><div className="space-y-1.5"><Label>Footer HTML (memorizzato come testo)</Label><Textarea rows={5} value={config.footer_html || ""} onChange={(event) => setConfig((current) => ({ ...current, footer_html: event.target.value }))} /></div><label className="flex items-center gap-2 text-sm"><Checkbox checked={Boolean(config.news_enabled)} onCheckedChange={(checked) => setConfig((current) => ({ ...current, news_enabled: checked === true }))} />Abilita raccolta news</label><Button disabled={Boolean(busy)} onClick={saveConfig}>{busy === "config-save" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Salva impostazioni</Button></CardContent></Card></TabsContent>
      </Tabs>

      <Dialog open={contactDialog} onOpenChange={setContactDialog}><DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{editingContact ? "Modifica contatto" : "Nuovo contatto"}</DialogTitle></DialogHeader><div className="grid gap-3 py-2"><div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={contactForm.email} onChange={(event) => setContactForm((current) => ({ ...current, email: event.target.value }))} /></div><div className="grid gap-3 sm:grid-cols-2"><div className="space-y-1.5"><Label>Nome</Label><Input value={contactForm.nome} onChange={(event) => setContactForm((current) => ({ ...current, nome: event.target.value }))} /></div><div className="space-y-1.5"><Label>Organizzazione</Label><Input value={contactForm.organizzazione} onChange={(event) => setContactForm((current) => ({ ...current, organizzazione: event.target.value }))} /></div><div className="space-y-1.5"><Label>Fonte *</Label><Input value={contactForm.fonte} onChange={(event) => setContactForm((current) => ({ ...current, fonte: event.target.value }))} /></div><div className="space-y-1.5"><Label>Base giuridica *</Label><Input placeholder="es. consenso, legittimo interesse" value={contactForm.base_giuridica} onChange={(event) => setContactForm((current) => ({ ...current, base_giuridica: event.target.value }))} /></div></div><div className="space-y-1.5"><Label>Tipo contatto</Label><Select value={contactForm.tipo_contatto || "prospect"} onValueChange={(value) => setContactForm((current) => ({ ...current, tipo_contatto: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="prospect">Prospect</SelectItem><SelectItem value="cliente">Cliente</SelectItem><SelectItem value="partner">Partner</SelectItem></SelectContent></Select></div><div className="space-y-1.5"><Label>Tag (separati da virgola)</Label><Input value={contactForm.tags || ""} onChange={(event) => setContactForm((current) => ({ ...current, tags: event.target.value }))} /></div></div><DialogFooter><Button variant="outline" onClick={() => setContactDialog(false)}>Annulla</Button><Button disabled={Boolean(busy)} onClick={saveContact}>Salva contatto</Button></DialogFooter></DialogContent></Dialog>
      <Dialog open={listDialog} onOpenChange={setListDialog}><DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{editingList ? "Modifica lista" : "Nuova lista"}</DialogTitle></DialogHeader><div className="space-y-3 py-2"><div className="space-y-1.5"><Label>Nome *</Label><Input value={listForm.nome} onChange={(event) => setListForm((current) => ({ ...current, nome: event.target.value }))} /></div><div className="space-y-1.5"><Label>Descrizione</Label><Textarea value={listForm.description} onChange={(event) => setListForm((current) => ({ ...current, description: event.target.value }))} /></div><div className="space-y-2"><Label>Contatti</Label><div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border p-3">{contacts.map((contact) => <label key={contact.id} className="flex items-center gap-2 text-sm"><Checkbox checked={listForm.contact_ids.includes(contact.id)} onCheckedChange={(checked) => setListForm((current) => ({ ...current, contact_ids: checked === true ? [...current.contact_ids, contact.id] : current.contact_ids.filter((id) => id !== contact.id) }))} />{contact.nome || contact.email} <span className="text-slate-400">{contact.email}</span></label>)}</div></div></div><DialogFooter><Button variant="outline" onClick={() => setListDialog(false)}>Annulla</Button><Button disabled={Boolean(busy)} onClick={saveList}>Salva lista</Button></DialogFooter></DialogContent></Dialog>
      <Dialog open={bandoDialog} onOpenChange={setBandoDialog}><DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{editingBando ? "Modifica bando" : "Nuovo bando"}</DialogTitle></DialogHeader><div className="grid gap-3 py-2"><div className="space-y-1.5"><Label>Titolo *</Label><Input value={bandoForm.titolo} onChange={(event) => setBandoForm((current) => ({ ...current, titolo: event.target.value }))} /></div><div className="grid gap-3 sm:grid-cols-2"><div className="space-y-1.5"><Label>Ente</Label><Input value={bandoForm.ente} onChange={(event) => setBandoForm((current) => ({ ...current, ente: event.target.value }))} /></div><div className="space-y-1.5"><Label>Regione</Label><Input value={bandoForm.regione} onChange={(event) => setBandoForm((current) => ({ ...current, regione: event.target.value }))} /></div><div className="space-y-1.5"><Label>Scadenza</Label><Input type="date" value={bandoForm.scadenza} onChange={(event) => setBandoForm((current) => ({ ...current, scadenza: event.target.value }))} /></div><div className="space-y-1.5"><Label>Importo</Label><Input value={bandoForm.importo} onChange={(event) => setBandoForm((current) => ({ ...current, importo: event.target.value }))} /></div></div><div className="space-y-1.5"><Label>URL</Label><Input type="url" value={bandoForm.url} onChange={(event) => setBandoForm((current) => ({ ...current, url: event.target.value }))} /></div><div className="space-y-1.5"><Label>Riassunto</Label><Textarea value={bandoForm.riassunto} onChange={(event) => setBandoForm((current) => ({ ...current, riassunto: event.target.value }))} /></div></div><DialogFooter><Button variant="outline" onClick={() => setBandoDialog(false)}>Annulla</Button><Button disabled={Boolean(busy)} onClick={saveBando}>Salva bando</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}
